import type {
  InfiniteData,
  MutationFunctionContext,
  QueryClient,
} from '@tanstack/react-query';

import type { z, ZodObject, ZodType } from 'zod/v4';

import type {
  AbstractEndpoint,
  AbstractStream,
  AnyEndpointConfig,
  AnyStreamConfig,
  BaseEndpointOptions,
  EndpointHandler,
  EndpointOptions,
  ErrorSchemaRecord,
  HttpMethod,
  StreamHandler,
} from '../index.js';
import type { ClientOptions as MintClientOptions } from '../types.js';
import type { ClientInstance } from './client/types.js';
import type { ComputeBaseResult } from './client/types/helpers.js';
import type { ClientOptions, ProcessResponseFunction } from './common/types.js';
import { makeMutation } from './mutation/make-hook.js';
import type { MutationArgs } from './mutation/types.js';
import { makeInfiniteQueryOptions } from './query/make-infinite-options.js';
import { makeQueryOptions } from './query/make-options.js';

// MINT-OVERRIDE: extends upstream QueryConfig/InfiniteQueryConfig/MutationConfig
// with pass-through fields that upstream's declareClient does NOT forward
// into `api.declareEndpoint`:
//   - `snakeCaseQueryParams` (legacy top-level flag; our builder's normalize()
//     lifts it into clientOptions.request.snakeCaseQueryParams).
//   - `clientOptions` (our typed nested {request?, response?} fields — upstream's
//     makeConfig flat-spreads these into the axios config; middlewares in
//     @mint-lib/api read them).
//   - `mock` (MINT-OVERRIDE: forward mock field for mockBuilder compatibility;
//     ignored by the real builder, consumed by `mockBuilder.declareEndpoint`).
// We re-implement declareClient so that each of query/infiniteQuery/mutation/
// multipartMutation includes these fields in the endpoint-options object
// passed to `api.declareEndpoint`.

type PassThrough = {
  snakeCaseQueryParams?: boolean;
  clientOptions?: MintClientOptions;
  // MINT-OVERRIDE: forward mock field for mockBuilder compatibility.
  mock?: (ctx: unknown) => unknown;
};

/**
 * Configuration for declaring a query endpoint.
 */
export interface QueryConfig<
  Method extends HttpMethod = HttpMethod,
  Url extends string = string,
  QuerySchema extends ZodObject | undefined = undefined,
  Response extends ZodType = ZodType,
  ErrorSchema extends ErrorSchemaRecord | undefined = undefined,
  Result = ComputeBaseResult<true, Response, ErrorSchema>,
  RequestSchema extends ZodType | undefined = undefined,
> {
  method: Method;
  url: Url;
  querySchema?: QuerySchema;
  responseSchema: Response;
  errorSchema?: ErrorSchema;
  requestSchema?: RequestSchema;
  processResponse?: (
    data: ComputeBaseResult<true, Response, ErrorSchema>,
  ) => Result;
  /** MINT-OVERRIDE: legacy top-level flag, normalized by builder. */
  snakeCaseQueryParams?: boolean;
  /** MINT-OVERRIDE: forwarded through to clientOptions for middlewares. */
  clientOptions?: MintClientOptions;
}

/**
 * Configuration for declaring an infinite query endpoint.
 */
export type InfiniteQueryConfig<
  Method extends HttpMethod = HttpMethod,
  Url extends string = string,
  QuerySchema extends ZodObject = ZodObject,
  Response extends ZodType = ZodType,
  ErrorSchema extends ErrorSchemaRecord | undefined = undefined,
  PageResult = ComputeBaseResult<true, Response, ErrorSchema>,
  Result = InfiniteData<PageResult>,
  RequestSchema extends ZodType | undefined = undefined,
> = {
  method: Method;
  url: Url;
  querySchema: QuerySchema;
  responseSchema: Response;
  errorSchema?: ErrorSchema;
  requestSchema?: RequestSchema;
  processResponse?: (
    data: ComputeBaseResult<true, Response, ErrorSchema>,
  ) => PageResult;
  select?: (data: InfiniteData<PageResult>) => Result;
  getNextPageParam: (
    lastPage: PageResult,
    allPages: PageResult[],
    lastPageParam: z.infer<QuerySchema> | undefined,
    allPageParams: z.infer<QuerySchema>[] | undefined,
  ) => z.input<QuerySchema> | undefined;
  getPreviousPageParam?: (
    firstPage: PageResult,
    allPages: PageResult[],
    lastPageParam: z.infer<QuerySchema> | undefined,
    allPageParams: z.infer<QuerySchema>[] | undefined,
  ) => z.input<QuerySchema>;
  initialPageParam?: z.input<QuerySchema>;
  /** MINT-OVERRIDE: legacy top-level flag, normalized by builder. */
  snakeCaseQueryParams?: boolean;
  /** MINT-OVERRIDE: forwarded through to clientOptions for middlewares. */
  clientOptions?: MintClientOptions;
};

/**
 * Configuration for declaring a mutation endpoint.
 */
export interface MutationConfig<
  Method extends 'POST' | 'PUT' | 'PATCH' | 'DELETE' =
    | 'POST'
    | 'PUT'
    | 'PATCH'
    | 'DELETE',
  Url extends string = string,
  RequestSchema extends ZodType | undefined = Method extends 'DELETE'
    ? undefined
    : ZodType,
  QuerySchema extends ZodObject | undefined = undefined,
  Response extends ZodType = ZodType,
  ErrorSchema extends ErrorSchemaRecord | undefined = undefined,
  ReqResult = ComputeBaseResult<true, Response, ErrorSchema>,
  Result = unknown,
  TOnMutateResult = unknown,
  Context = unknown,
  UseKey extends boolean = false,
> {
  method: Method;
  url: Url;
  querySchema?: QuerySchema;
  responseSchema: Response;
  errorSchema?: ErrorSchema;
  requestSchema?: RequestSchema;
  processResponse: ProcessResponseFunction<Result, ReqResult>;
  useContext?: () => Context;
  // MINT-OVERRIDE: onSuccess/onError/onSettled use legacy signature with
  // `queryClient` as first argument; the wrapper transforms at call time
  // using `context.client` from MutationFunctionContext.
  onSuccess?: (
    queryClient: QueryClient,
    data: Result,
    variables: MutationArgs<Url, RequestSchema, QuerySchema>,
    context: Context &
      MutationFunctionContext & { onMutateResult: TOnMutateResult | undefined },
  ) => void | Promise<void>;
  onError?: (
    queryClient: QueryClient,
    err: unknown,
    variables: MutationArgs<Url, RequestSchema, QuerySchema>,
    context: Context &
      MutationFunctionContext & { onMutateResult: TOnMutateResult | undefined },
  ) => void | Promise<void>;
  onMutate?: (
    variables: MutationArgs<Url, RequestSchema, QuerySchema>,
    context: Context & MutationFunctionContext,
  ) => TOnMutateResult | Promise<TOnMutateResult>;
  onSettled?: (
    queryClient: QueryClient,
    data: Result | undefined,
    error: Error | null,
    variables: MutationArgs<Url, RequestSchema, QuerySchema>,
    context: Context &
      MutationFunctionContext & { onMutateResult: TOnMutateResult | undefined },
  ) => void | Promise<void>;
  useKey?: UseKey;
  meta?: Record<string, unknown>;
  /** MINT-OVERRIDE: legacy top-level flag, normalized by builder. */
  snakeCaseQueryParams?: boolean;
  /** MINT-OVERRIDE: forwarded through to clientOptions for middlewares. */
  clientOptions?: MintClientOptions;
}

/**
 * Creates a client instance for making type-safe queries and mutations.
 *
 * MINT-OVERRIDE: wraps upstream's declareClient so that each of
 * query / infiniteQuery / mutation / multipartMutation forwards two extra
 * fields from the config into the underlying `api.declareEndpoint` call:
 *   - `snakeCaseQueryParams` (legacy top-level flag; our builder's normalize
 *     lifts it into clientOptions.request.snakeCaseQueryParams).
 *   - `clientOptions` (typed nested `{request?, response?}` fields read by
 *     @mint-lib/api middlewares via upstream's makeConfig flat spread).
 *
 * @template UseDiscriminator - When `true`, errors are returned as union types.
 *   When `false` (default), errors are thrown and not included in TData.
 *
 * @param options - Client configuration including the API builder and defaults
 * @returns A client instance with query, infiniteQuery, and mutation methods
 *
 * @example
 * ```typescript
 * const api = builder({});
 * const client = declareClient({ api });
 *
 * const getUser = client.query({
 *   method: 'GET',
 *   url: '/users/$id',
 *   responseSchema: UserSchema,
 * });
 *
 * // In a component
 * const { data } = useSuspenseQuery(getUser({ urlParams: { id: '123' } }));
 * ```
 */
export function declareClient<UseDiscriminator extends boolean = false>({
  api,
  defaults = {},
}: ClientOptions<UseDiscriminator>): ClientInstance<UseDiscriminator> {
  function pickPassThrough(config: PassThrough): PassThrough {
    const out: PassThrough = {};
    if (config.snakeCaseQueryParams !== undefined) {
      out.snakeCaseQueryParams = config.snakeCaseQueryParams;
    }
    if (config.clientOptions !== undefined) {
      out.clientOptions = config.clientOptions;
    }
    // MINT-OVERRIDE: forward mock field for mockBuilder compatibility.
    if (config.mock !== undefined) {
      out.mock = config.mock;
    }
    return out;
  }

  function query(config: QueryConfig) {
    const endpoint = api.declareEndpoint({
      method: config.method,
      url: config.url,
      querySchema: config.querySchema,
      requestSchema: config.requestSchema,
      responseSchema: config.responseSchema,
      errorSchema: config.errorSchema,
      ...pickPassThrough(config),
    });

    const queryOptions = makeQueryOptions(endpoint, {
      ...defaults,
      processResponse: config.processResponse ?? ((data) => data),
    });
    // @ts-expect-error We attach the endpoint to the queryOptions
    queryOptions.endpoint = endpoint;
    return queryOptions;
  }

  function queryFromEndpoint(
    endpoint:
      | AbstractEndpoint<AnyEndpointConfig>
      | EndpointHandler<EndpointOptions, UseDiscriminator>,
    options?: {
      processResponse?: (
        data: z.output<AnyEndpointConfig['responseSchema']>,
      ) => unknown;
    },
  ) {
    return makeQueryOptions(endpoint as any, {
      ...defaults,
      processResponse: options?.processResponse ?? ((data) => data),
    });
  }

  function infiniteQuery(config: InfiniteQueryConfig) {
    const endpoint = api.declareEndpoint({
      method: config.method,
      url: config.url,
      querySchema: config.querySchema,
      requestSchema: config.requestSchema,
      responseSchema: config.responseSchema,
      errorSchema: config.errorSchema,
      ...pickPassThrough(config),
    });
    const infiniteQueryOptions = makeInfiniteQueryOptions(endpoint, {
      ...defaults,
      processResponse: config.processResponse ?? ((data: unknown) => data),
      getNextPageParam: config.getNextPageParam,
      getPreviousPageParam: config.getPreviousPageParam,
      initialPageParam: config.initialPageParam,
    });

    // @ts-expect-error We attach the endpoint to the infiniteQueryOptions
    infiniteQueryOptions.endpoint = endpoint;
    return infiniteQueryOptions;
  }

  function infiniteQueryFromEndpoint(
    endpoint:
      | AbstractEndpoint<AnyEndpointConfig>
      | EndpointHandler<EndpointOptions, UseDiscriminator>,
    options: {
      processResponse?: (
        data: z.output<AnyEndpointConfig['responseSchema']>,
      ) => unknown;
      getNextPageParam: (
        lastPage: z.infer<AnyEndpointConfig['responseSchema']>,
        allPages: z.infer<AnyEndpointConfig['responseSchema']>[],
        lastPageParam: z.infer<AnyEndpointConfig['querySchema']> | undefined,
        allPageParams: z.infer<AnyEndpointConfig['querySchema']>[] | undefined,
      ) => z.input<AnyEndpointConfig['querySchema']> | undefined;
      getPreviousPageParam?: (
        firstPage: z.infer<AnyEndpointConfig['responseSchema']>,
        allPages: z.infer<AnyEndpointConfig['responseSchema']>[],
        lastPageParam: z.infer<AnyEndpointConfig['querySchema']> | undefined,
        allPageParams: z.infer<AnyEndpointConfig['querySchema']>[] | undefined,
      ) => z.input<AnyEndpointConfig['querySchema']>;
      initialPageParam?: z.input<AnyEndpointConfig['querySchema']>;
    },
  ) {
    return makeInfiniteQueryOptions(endpoint, {
      ...defaults,
      processResponse: options?.processResponse ?? ((data) => data),
      getNextPageParam: options.getNextPageParam,
      getPreviousPageParam: options?.getPreviousPageParam,
      initialPageParam: options?.initialPageParam,
    });
  }

  function mutation(config: MutationConfig) {
    const endpoint = api.declareEndpoint({
      method: config.method,
      url: config.url,
      querySchema: config.querySchema,
      requestSchema: config.requestSchema,
      responseSchema: config.responseSchema,
      errorSchema: config.errorSchema,
      ...pickPassThrough(config),
    });

    // MINT-OVERRIDE: compat shim — consumers use OLD signature
    // `onSuccess(queryClient, data, variables, context)`, upstream expects NEW
    // `onSuccess(data, variables, context)`. We wrap to extract `queryClient`
    // from `context.client` (react-query v5's MutationFunctionContext.client)
    // and prepend it to the user-provided callback.
    const userOnSuccess = config.onSuccess;
    const userOnError = config.onError;
    const userOnSettled = config.onSettled;
    const wrappedOnSuccess = userOnSuccess
      ? (data: any, variables: any, context: any) =>
          userOnSuccess(context.client, data, variables, context)
      : undefined;
    const wrappedOnError = userOnError
      ? (err: any, variables: any, context: any) =>
          userOnError(context.client, err, variables, context)
      : undefined;
    const wrappedOnSettled = userOnSettled
      ? (data: any, error: any, variables: any, context: any) =>
          userOnSettled(context.client, data, error, variables, context)
      : undefined;

    // @ts-expect-error Type inference for errorSchema variants
    const useMutation = makeMutation(endpoint, {
      processResponse: config.processResponse ?? ((data: unknown) => data),
      useContext: config.useContext,
      onMutate: config.onMutate,
      onSuccess: wrappedOnSuccess,
      onError: wrappedOnError,
      onSettled: wrappedOnSettled,
      useKey: config.useKey,
      meta: config.meta,
      ...defaults,
    });

    // @ts-expect-error We attach the endpoint to the useMutation
    useMutation.endpoint = endpoint;
    return useMutation;
  }

  function mutationFromEndpoint(
    endpoint:
      | AbstractEndpoint<AnyEndpointConfig>
      | AbstractStream<AnyStreamConfig>
      | EndpointHandler<EndpointOptions, UseDiscriminator>
      | StreamHandler<BaseEndpointOptions, UseDiscriminator>,
    options?: {
      processResponse?: ProcessResponseFunction;
      useContext?: () => unknown;
      onMutate?: (
        variables: MutationArgs,
        context: MutationFunctionContext & { [key: string]: unknown },
      ) => unknown | Promise<unknown>;
      onSuccess?: (
        data: unknown,
        variables: MutationArgs,
        context: MutationFunctionContext & {
          onMutateResult: unknown | undefined;
          [key: string]: unknown;
        },
      ) => void | Promise<void>;
      onError?: (
        err: unknown,
        variables: MutationArgs,
        context: MutationFunctionContext & {
          onMutateResult: unknown | undefined;
          [key: string]: unknown;
        },
      ) => void | Promise<void>;
      onSettled?: (
        data: unknown | undefined,
        error: Error | null,
        variables: MutationArgs,
        context: MutationFunctionContext & {
          onMutateResult: unknown | undefined;
          [key: string]: unknown;
        },
      ) => void | Promise<void>;
      useKey?: boolean;
      meta?: Record<string, unknown>;
    },
  ) {
    // @ts-expect-error endpoint types are compatible at runtime
    return makeMutation(endpoint, {
      processResponse: options?.processResponse,
      useContext: options?.useContext,
      onMutate: options?.onMutate,
      onSuccess: options?.onSuccess,
      onError: options?.onError,
      onSettled: options?.onSettled,
      useKey: options?.useKey,
      meta: options?.meta,
      ...defaults,
    });
  }

  function multipartMutation(config: MutationConfig) {
    const endpoint = api.declareMultipart({
      method: config.method,
      url: config.url,
      querySchema: config.querySchema,
      requestSchema: config.requestSchema,
      responseSchema: config.responseSchema,
      errorSchema: config.errorSchema,
      ...pickPassThrough(config),
    });

    // MINT-OVERRIDE: same compat shim as `mutation()` above — prepend
    // `context.client` as the first arg so consumer callbacks see the
    // legacy `(queryClient, data/err, variables, context)` signature.
    const userOnSuccess = config.onSuccess;
    const userOnError = config.onError;
    const userOnSettled = config.onSettled;
    const wrappedOnSuccess = userOnSuccess
      ? (data: any, variables: any, context: any) =>
          userOnSuccess(context.client, data, variables, context)
      : undefined;
    const wrappedOnError = userOnError
      ? (err: any, variables: any, context: any) =>
          userOnError(context.client, err, variables, context)
      : undefined;
    const wrappedOnSettled = userOnSettled
      ? (data: any, error: any, variables: any, context: any) =>
          userOnSettled(context.client, data, error, variables, context)
      : undefined;

    // @ts-expect-error Type inference for errorSchema variants
    const useMutation = makeMutation(endpoint, {
      processResponse: config.processResponse ?? ((data: unknown) => data),
      useContext: config.useContext,
      onSuccess: wrappedOnSuccess,
      onError: wrappedOnError,
      onMutate: config.onMutate,
      onSettled: wrappedOnSettled,
      useKey: config.useKey,
      ...defaults,
    });

    // @ts-expect-error We attach the endpoint to the useMutation
    useMutation.endpoint = endpoint;
    return useMutation;
  }

  return {
    // @ts-expect-error We simplified types here
    query,
    // @ts-expect-error We simplified types here
    queryFromEndpoint,
    // @ts-expect-error We simplified types here
    infiniteQuery,
    // @ts-expect-error We simplified types here
    infiniteQueryFromEndpoint,
    // @ts-expect-error We simplified types here
    mutation,
    // @ts-expect-error We simplified types here
    mutationFromEndpoint,
    // @ts-expect-error We simplified types here
    multipartMutation,
  };
}
